import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

function RegisterInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({ email: '', username: '', password: '', full_name: '' });

  useEffect(() => {
    if (!token) {
      setError('No invite token provided.');
      setLoading(false);
      return;
    }
    const validate = async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setInvite(data);
        if (data.email) {
          setFormData(prev => ({ ...prev, email: data.email }));
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired invite link.');
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/auth/register-invite', { ...formData, token });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed.');
    }
  };

  const roleLabels = {
    member: 'Member',
    volunteer: 'Volunteer',
    ap_admin: 'AP Admin',
    guest: 'Guest'
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'var(--bg-apex)' }}>
        <div className="glass-card" style={{ padding: '3rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-body)' }}>Validating invite...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: 'var(--bg-apex)' }}>
        <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '440px', padding: '3rem', textAlign: 'center' }}>
          <div style={{
            background: 'rgba(34, 197, 94, 0.15)', width: '80px', height: '80px', borderRadius: '50%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem'
          }}>
            <span style={{ fontSize: '2.5rem' }}>&#10003;</span>
          </div>
          <h2 style={{ color: 'var(--text-heading)', marginBottom: '1rem' }}>Registration Complete</h2>
          <p style={{ color: 'var(--text-body)', marginBottom: '2rem' }}>Your account has been created. You can now log in.</p>
          <Link to="/login" className="btn-cyber" style={{ display: 'inline-block', padding: '1rem 2.5rem', textDecoration: 'none' }}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      background: 'var(--bg-apex)',
      backgroundImage: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.05), transparent 70%)'
    }}>
      <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '440px', padding: '3rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{
            background: 'var(--accent-primary)',
            width: '80px', height: '80px', borderRadius: '20px', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', marginBottom: '2rem',
            boxShadow: 'var(--glow-indigo)'
          }}>
            <span style={{ fontWeight: 900, color: 'var(--btn-text)', fontSize: '2.5rem' }}>A</span>
          </div>
          <h2 style={{ fontSize: '2rem', color: 'var(--text-heading)', marginBottom: '0.75rem', letterSpacing: '-0.03em' }}>
            Join AP NEO
          </h2>
          {invite && (
            <p style={{ color: 'var(--text-body)', fontSize: '0.95rem', fontWeight: 500 }}>
              You've been invited as <strong style={{ color: 'var(--accent-primary)' }}>{roleLabels[invite.role] || invite.role}</strong>
              {invite.companyName && <> for <strong style={{ color: 'var(--accent-cyan)' }}>{invite.companyName}</strong></>}
            </p>
          )}
        </div>

        {error && (
          <div style={{
            background: 'rgba(244, 114, 182, 0.1)', border: '1px solid rgba(244, 114, 182, 0.2)',
            color: 'var(--accent-magenta)', padding: '1rem', borderRadius: '12px',
            marginBottom: '1.5rem', textAlign: 'center', fontSize: '0.85rem', fontWeight: 600
          }}>
            {error}
          </div>
        )}

        {invite ? (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: '0.5rem' }}>Full Name</label>
              <input
                className="cyber-input"
                type="text"
                placeholder="Your full name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: '0.5rem' }}>Email Address</label>
              <input
                className="cyber-input"
                type="email"
                placeholder="you@example.com"
                required
                value={formData.email}
                readOnly={!!invite.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={invite.email ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: '0.5rem' }}>Username</label>
              <input
                className="cyber-input"
                type="text"
                placeholder="Choose a username"
                required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: '0.5rem' }}>Password</label>
              <input
                className="cyber-input"
                type="password"
                placeholder="Min. 6 characters"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>

            <button type="submit" className="btn-cyber" style={{ marginTop: '1rem', padding: '1.25rem' }}>
              Create Account
            </button>
          </form>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Link to="/login" style={{ color: 'var(--accent-primary)', fontWeight: 600 }}>Back to Login</Link>
          </div>
        )}

        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 500 }}>
            Already have an account?
            <Link to="/login" style={{ color: 'var(--accent-primary)', fontWeight: 700, marginLeft: '0.5rem', textDecoration: 'none' }}>
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default RegisterInvite;
